import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch } from './client';

export interface AdminPatientDetail {
  id: string;
  organisation_id: string;
  given_name: string;
  family_name: string;
  dob: string;
  sex_at_birth?: string;
  gender_identity?: string;
  postal_code?: string;
  email?: string;
  phone?: string;
  status: string;
  identifiers: Array<{ scheme: string; value: string }>;
  created_at: string;
  updated_at: string;
}

const KEYS = {
  detail: (id: string) => ['patients', 'detail', id] as const,
};

export function usePatient(id: string | undefined) {
  return useQuery({
    queryKey: id ? KEYS.detail(id) : ['patients', 'detail', 'idle'],
    queryFn: () => apiGet<AdminPatientDetail>(`/api/patients/${id}`),
    enabled: !!id,
  });
}

export interface UpdatePatientInput {
  givenName?: string;
  familyName?: string;
  dob?: string;
  sexAtBirth?: string;
  postalCode?: string;
  email?: string;
  phone?: string;
  genderIdentity?: string;
  reason: string;
}

export function useUpdatePatient(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePatientInput) =>
      apiPatch<AdminPatientDetail>(`/api/patients/${patientId}`, {
        given_name: input.givenName,
        family_name: input.familyName,
        dob: input.dob,
        sex_at_birth: input.sexAtBirth,
        postal_code: input.postalCode,
        email: input.email,
        phone: input.phone,
        gender_identity: input.genderIdentity,
        reason: input.reason,
      }),
    onSuccess: (data) => {
      qc.setQueryData(KEYS.detail(patientId), data);
      void qc.invalidateQueries({ queryKey: KEYS.detail(patientId) });
    },
  });
}
